import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import UserContext from "../../../../contexts/UserContext";
import SupplierContext from "../../../../contexts/SupplierContext";
import IErrorDTO from "../../../../models/DTO/IErrorDTO";
import OwButton from "../../../shared/OwButton/OwButton";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import { OwIconButton } from "../../../shared/OwIconButton/OwIconButton";
import UserService from "../../../../services/UserService";
import { OwAccordion } from "../../../shared/OwAccordion/OwAccordion";
import logo from "../../../../assets/images/bigcommerce/bigcommerce-logo.svg";
import Modal from "react-modal";
import IBigCommerceGetStoresDTO from "../../../../models/DTO/IBigCommerceGetStoresDTO";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import IBigCommerceOnGetStoresDTO from "../../../../models/DTO/IBigCommerceOnGetStoresDTO";
import IBigCommerceDeleteStoreDTO from "../../../../models/DTO/IBigCommerceDeleteStoreDTO";
import IBigCommerce from "../../../../models/IBigCommerce";
import { BigCommerceStoreModal } from "./BigCommerceStoreModal/BigCommerceStoreModal";
import INotification from "../../../../models/INotification";
import NotificationPositionEnum from "../../../../models/NotificationPositionEnum";
import NotificationLevelEnum from "../../../../models/NotificationLevelEnum";
import ILoadUserDTO from "../../../../models/DTO/ILoadUserDTO";
import { Socket } from "socket.io-client";

type Props = {
    setActivityIndicator: (activityIndicator: boolean) => void;
    socket: Socket;
    notifyFn: (data: IErrorDTO | null, _notification?: any, isFormatted?: boolean) => void;
};

export const BigCommerceConnect: React.FC<Props> = (props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);

    const hasBigCommerce = supplierModel.Supplier_Integration.has_bigcommerce;

    const getModalStyle = () => {
        return {
            content: {
                top: "10px",
                left: "10px",
                right: "10px",
                bottom: "10px",
                color: "var(--main-text-color)",
                backgroundColor: "var(--main-bg-color)",
                border: "1px solid var(--minor-bg-color)",
            },
            overlay: {
                zIndex: 100,
            },
        };
    };

    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [modalStyle, setModalStyle] = useState(getModalStyle());
    const [modalComponent, setModalComponent] = useState<any>();
    const [stores, setStores] = useState<IBigCommerce[]>([]);

    useEffect(() => {
        props.socket.on(EndpointsEnum.BigCommerceOnGetStores, onGetBigCommerceStores);
        props.socket.on(EndpointsEnum.BigCommerceOnDeleteStore, onDeleteStore);

        return () => {
            props.socket.removeListener(EndpointsEnum.BigCommerceOnGetStores, onGetBigCommerceStores);
            props.socket.removeListener(EndpointsEnum.BigCommerceOnDeleteStore, onDeleteStore);
        };
    }, []);

    const addBigCommerceModal = () => {
        const component = <BigCommerceStoreModal closeModalFn={closeModal} socket={props.socket} notifyFn={props.notifyFn} />;

        setModalComponent(component);
        setModalIsOpen(true);
    };

    const closeModal = () => {
        setModalIsOpen(false);
        setModalComponent(null);
        loadUser();
    };

    const getBigCommerceStores = () => {
        const payload: IBigCommerceGetStoresDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        props.socket.emit(EndpointsEnum.BigCommerceGetStores, payload);
    };

    const onGetBigCommerceStores = (data: IBigCommerceOnGetStoresDTO) => {
        setStores(data.stores);
    };

    const editStore = (store: IBigCommerce) => {
        const component = <BigCommerceStoreModal closeModalFn={closeModal} socket={props.socket} notifyFn={props.notifyFn} store={store} />;

        setModalComponent(component);
        setModalIsOpen(true);
    };

    const confirmDelete = (store: IBigCommerce, e?: ChangeEvent<any>) => {
        const payload: IBigCommerceDeleteStoreDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            storeGuid: store.guid,
        };

        props.socket.emit(EndpointsEnum.BigCommerceDeleteStore, payload);
    };

    const onDeleteStore = () => {
        const notification: INotification = {
            message: "successfully_deleted_store",
            position: NotificationPositionEnum.bc,
            level: NotificationLevelEnum.success,
        };

        props.notifyFn(null, notification);

        getBigCommerceStores();
        loadUser();
    };

    const loadUser = () => {
        const payload: ILoadUserDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
        };

        props.socket.emit(EndpointsEnum.AppLoadUser, payload);
    };

    return (
        <div className={"BigCommerceConnect"}>
            <OwAccordion
                hideChevron={!hasBigCommerce}
                leftImage={logo}
                leftImageAlt={"BigCommerce"}
                leftImageHeight={30}
                titleClick={hasBigCommerce}
                fn={getBigCommerceStores}
                rightElem={
                    <div className={"ms-auto"}>
                        <OwButton title={"add_bigcommerce_shop"} fn={addBigCommerceModal} />
                    </div>
                }>
                <div className={"title d-flex"}>
                    <div>
                        {hasBigCommerce ? (
                            <div>
                                <div className={"d-flex"}>
                                    <div className="w-10r"></div>
                                    <div className="w-20r">
                                        <OwLabel title={"name"} isBold={true} />
                                    </div>
                                    <div className="w-10r">
                                        <OwLabel title={"catalog"} isBold={true} />
                                    </div>
                                    <div className="w-10r">
                                        <OwLabel title={"price_group"} isBold={true} />
                                    </div>
                                    <div className="w-15r">
                                        <OwLabel title={"last_pulled_at"} isBold={true} />
                                    </div>
                                    <div className="w-15r">
                                        <OwLabel title={"active"} isBold={true} />
                                    </div>
                                </div>
                                {(stores || []).map((store) => {
                                    return (
                                        <div key={store.guid} className={"d-flex w-100"}>
                                            <div className={"w-10r"}>
                                                <OwIconButton
                                                    iconClass={"fad fa-pencil me-2"}
                                                    iconColorClass={"text-primary"}
                                                    fn={(e?: ChangeEvent<any>) => {
                                                        editStore(store);
                                                    }}
                                                />
                                                <OwIconButton
                                                    iconClass={"fad fa-trash-alt"}
                                                    iconColorClass={"text-danger"}
                                                    fn={(e?: ChangeEvent<any>) => {
                                                        confirmDelete(store, e);
                                                    }}
                                                />
                                            </div>
                                            <div className={"w-20r"}>{store.name}</div>
                                            <div className={"w-10r"}>{store.Catalog?.name}</div>
                                            <div className={"w-10r"}>{store.Price_Group?.value}</div>
                                            <div className="w-15r">
                                                {store.last_pulled_at
                                                    ? UserService.parseLastUpdated(store.last_pulled_at, true, true, true)
                                                    : "N/A"}
                                            </div>
                                            <div>
                                                <OwLabel
                                                    leftIconClass={store.active ? "fas fa-check" : "fas fa-times"}
                                                    className={store.active ? "text-success" : "text-danger"}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : null}
                    </div>
                </div>
            </OwAccordion>

            <div>
                <Modal
                    isOpen={modalIsOpen}
                    onRequestClose={closeModal}
                    contentLabel="Orderwerks"
                    shouldCloseOnOverlayClick={false}
                    className={"modalContentClass"}
                    overlayClassName={"modalOverlayClass"}
                    style={modalStyle}>
                    {modalComponent}
                </Modal>
            </div>
        </div>
    );
};
